import * as React from 'react';
import camelCaseKeys from 'camelcase-keys';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  countActiveProjectSearchFilters,
  emptyProjectSearchFilters,
  getProjectSearchDateBounds,
  ProjectSearchDateOperator,
  ProjectSearchFilters,
  ProjectSearchVisibilityFilter,
  readProjectSearchVisibility
} from '../../../../../clients/shared/ProjectSearch';
import Config from 'models/Config';
import { post } from 'utils/fetch';

import { GetUserTasksData } from '../queries';
import { OrderBy, UserTaskFilter, UserTaskOrder } from '../types';
export { NEW_DASHBOARD_SEARCH_EXPERIMENT } from './newDashboardSearch';

export type DashboardTaskScope = 'my_tasks' | 'all_tasks';
export type DashboardTaskSortField =
  | 'project_name'
  | 'status_name'
  | 'requester_name'
  | 'created_at'
  | 'due_at'
  | 'updated_at';

export type DashboardTaskSearchFilters = ProjectSearchFilters & {
  project: string[];
};

export type DashboardTaskSearchState = {
  query: string;
  filters: DashboardTaskSearchFilters;
  scope: DashboardTaskScope;
  sortField: DashboardTaskSortField;
  sortDirection: OrderBy;
  page: number;
};

export type DashboardTaskSearchPagination = {
  filteredCount: number;
  unfilteredCount: number;
  page: number;
  perPage: number;
  totalPages: number;
};

type DashboardTaskSearchResponse = {
  tasks: GetUserTasksData['currentUser']['userTasks'];
  pagination: DashboardTaskSearchPagination;
};

type DashboardTaskSearchRequest = {
  query: string;
  scope: DashboardTaskScope;
  sort_field: DashboardTaskSortField;
  sort_direction: OrderBy;
  page: number;
  per_page: number;
  page_url: string;
  assignee: string[];
  created_at: string;
  created_at_end: string;
  created_at_operator: ProjectSearchDateOperator;
  created_at_start: string;
  created_by: string[];
  due_date: string;
  due_date_end: string;
  due_date_operator: ProjectSearchDateOperator;
  due_date_start: string;
  project: string[];
  severity: string[];
  status: string[];
  tag: string[];
  tag_match: ProjectSearchFilters['tagMatch'];
  visibility: ProjectSearchVisibilityFilter;
};

const QUERY_PARAM = 'dashboard_search_query';
const PROJECT_PARAM = 'dashboard_search_project';
const ASSIGNEE_PARAM = 'dashboard_search_assignee';
const CREATED_AT_PARAM = 'dashboard_search_created_at';
const CREATED_AT_OPERATOR_PARAM = 'dashboard_search_created_at_operator';
const CREATED_BY_PARAM = 'dashboard_search_created_by';
const DUE_DATE_PARAM = 'dashboard_search_due_date';
const DUE_DATE_OPERATOR_PARAM = 'dashboard_search_due_date_operator';
const PAGE_URL_PARAM = 'dashboard_search_page_url';
const SEVERITY_PARAM = 'dashboard_search_severity';
const STATUS_PARAM = 'dashboard_search_status';
const TAG_PARAM = 'dashboard_search_tag';
const TAG_MATCH_PARAM = 'dashboard_search_tag_match';
const VISIBILITY_PARAM = 'dashboard_search_visibility';
const SCOPE_PARAM = 'dashboard_search_scope';
const SORT_FIELD_PARAM = 'dashboard_search_sort_field';
const SORT_DIRECTION_PARAM = 'dashboard_search_sort_direction';
const PAGE_PARAM = 'dashboard_search_page';

const DASHBOARD_SEARCH_PARAMS = [
  QUERY_PARAM,
  PROJECT_PARAM,
  ASSIGNEE_PARAM,
  CREATED_AT_PARAM,
  CREATED_AT_OPERATOR_PARAM,
  CREATED_BY_PARAM,
  DUE_DATE_PARAM,
  DUE_DATE_OPERATOR_PARAM,
  PAGE_URL_PARAM,
  SEVERITY_PARAM,
  STATUS_PARAM,
  TAG_PARAM,
  TAG_MATCH_PARAM,
  VISIBILITY_PARAM,
  SCOPE_PARAM,
  SORT_FIELD_PARAM,
  SORT_DIRECTION_PARAM,
  PAGE_PARAM
];

const SORT_FIELDS: DashboardTaskSortField[] = [
  'project_name',
  'status_name',
  'requester_name',
  'created_at',
  'due_at',
  'updated_at'
];

const TABLE_SORT_FIELDS: Record<string, DashboardTaskSortField> = {
  projectName: 'project_name',
  statusName: 'status_name',
  requesterName: 'requester_name',
  createdAt: 'created_at',
  dueAt: 'due_at',
  updatedAt: 'updated_at'
};

const ENDPOINT_SORT_FIELDS = Object.entries(TABLE_SORT_FIELDS).reduce<
  Record<DashboardTaskSortField, string>
>((fields, [tableField, endpointField]) => {
  fields[endpointField] = tableField;
  return fields;
}, {} as Record<DashboardTaskSortField, string>);

export const emptyDashboardTaskSearchFilters = (): DashboardTaskSearchFilters => ({
  ...emptyProjectSearchFilters(),
  project: []
});

export const countActiveDashboardTaskSearchFilters = (
  filters: DashboardTaskSearchFilters
) => countActiveProjectSearchFilters(filters) + filters.project.length;

const readTokens = (params: URLSearchParams, name: string) =>
  params
    .getAll(name)
    .map(value => value.trim())
    .filter(Boolean);

const readDateOperator = (operator: string | null): ProjectSearchDateOperator =>
  operator === 'before' || operator === 'after' ? operator : 'on';

const readPositiveInteger = (value: string | null, fallback: number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

export const getDashboardTaskSearchState = (
  search: string
): DashboardTaskSearchState => {
  const params = new URLSearchParams(search);
  const scope = params.get(SCOPE_PARAM);
  const sortField = params.get(SORT_FIELD_PARAM);
  const sortDirection = params.get(SORT_DIRECTION_PARAM);

  return {
    query: params.get(QUERY_PARAM) || '',
    filters: {
      ...emptyDashboardTaskSearchFilters(),
      project: readTokens(params, PROJECT_PARAM),
      assignee: readTokens(params, ASSIGNEE_PARAM),
      createdAt: params.get(CREATED_AT_PARAM) || '',
      createdAtOperator: readDateOperator(
        params.get(CREATED_AT_OPERATOR_PARAM)
      ),
      createdBy: readTokens(params, CREATED_BY_PARAM),
      dueDate: params.get(DUE_DATE_PARAM) || '',
      dueDateOperator: readDateOperator(params.get(DUE_DATE_OPERATOR_PARAM)),
      page: params.get(PAGE_URL_PARAM) || '',
      severity: readTokens(params, SEVERITY_PARAM),
      status: readTokens(params, STATUS_PARAM),
      tag: readTokens(params, TAG_PARAM),
      tagMatch: params.get(TAG_MATCH_PARAM) === 'all' ? 'all' : 'any',
      visibility: readProjectSearchVisibility(params.get(VISIBILITY_PARAM))
    },
    scope: scope === 'all_tasks' ? 'all_tasks' : 'my_tasks',
    sortField: SORT_FIELDS.includes(sortField as DashboardTaskSortField)
      ? (sortField as DashboardTaskSortField)
      : 'project_name',
    sortDirection: sortDirection === OrderBy.DESC ? OrderBy.DESC : OrderBy.ASC,
    page: readPositiveInteger(params.get(PAGE_PARAM), 1)
  };
};

const appendTokens = (
  params: URLSearchParams,
  name: string,
  values: string[]
) => values.forEach(value => params.append(name, value));

export const dashboardTaskSearchUrl = ({
  pathname,
  search,
  hash,
  state
}: {
  pathname: string;
  search: string;
  hash: string;
  state: DashboardTaskSearchState;
}) => {
  const params = new URLSearchParams(search);
  DASHBOARD_SEARCH_PARAMS.forEach(name => params.delete(name));

  if (state.query) params.set(QUERY_PARAM, state.query);
  appendTokens(params, PROJECT_PARAM, state.filters.project);
  appendTokens(params, ASSIGNEE_PARAM, state.filters.assignee);
  if (state.filters.createdAt) {
    params.set(CREATED_AT_PARAM, state.filters.createdAt);
    params.set(CREATED_AT_OPERATOR_PARAM, state.filters.createdAtOperator);
  }
  appendTokens(params, CREATED_BY_PARAM, state.filters.createdBy);
  if (state.filters.dueDate) {
    params.set(DUE_DATE_PARAM, state.filters.dueDate);
    params.set(DUE_DATE_OPERATOR_PARAM, state.filters.dueDateOperator);
  }
  if (state.filters.page) params.set(PAGE_URL_PARAM, state.filters.page);
  appendTokens(params, SEVERITY_PARAM, state.filters.severity);
  appendTokens(params, STATUS_PARAM, state.filters.status);
  appendTokens(params, TAG_PARAM, state.filters.tag);
  if (state.filters.tag.length && state.filters.tagMatch === 'all') {
    params.set(TAG_MATCH_PARAM, state.filters.tagMatch);
  }
  if (state.filters.visibility) {
    params.set(VISIBILITY_PARAM, state.filters.visibility);
  }
  if (state.scope !== 'my_tasks') params.set(SCOPE_PARAM, state.scope);
  if (state.sortField !== 'project_name') {
    params.set(SORT_FIELD_PARAM, state.sortField);
  }
  if (state.sortDirection !== OrderBy.ASC) {
    params.set(SORT_DIRECTION_PARAM, state.sortDirection);
  }
  if (state.page !== 1) params.set(PAGE_PARAM, `${state.page}`);

  const nextSearch = params.toString();
  return `${pathname}${nextSearch ? `?${nextSearch}` : ''}${hash}`;
};

export const DASHBOARD_SEARCH_DEBOUNCE_MS = 300;

export const normalizeDashboardSearchQuery = (query: string) => query.trim();

/** Auto-search (debounced): 3+ characters or an all-numeric task ID. */
export const isDashboardAutoSearchQueryValid = (query: string) => {
  const normalizedQuery = normalizeDashboardSearchQuery(query);
  return /^\d+$/.test(normalizedQuery) || normalizedQuery.length >= 3;
};

/** Enter submit: 2+ characters or an all-numeric task ID. */
export const isDashboardSubmitSearchQueryValid = (query: string) => {
  const normalizedQuery = normalizeDashboardSearchQuery(query);
  return /^\d+$/.test(normalizedQuery) || normalizedQuery.length >= 2;
};

const isCommittedQuerySendable = (query: string) =>
  query === '' || /^\d+$/.test(query) || query.length >= 2;

export const dashboardTaskSearchRequest = (
  state: DashboardTaskSearchState,
  perPage: number
): DashboardTaskSearchRequest => {
  const query = state.query.trim();
  const createdAtBounds = getProjectSearchDateBounds(state.filters.createdAt);
  const dueDateBounds = getProjectSearchDateBounds(state.filters.dueDate);

  return {
    query: isCommittedQuerySendable(query) ? query : '',
    scope: state.scope,
    sort_field: state.sortField,
    sort_direction: state.sortDirection,
    page: state.page,
    per_page: perPage,
    page_url: state.filters.page,
    assignee: state.filters.assignee,
    created_at: state.filters.createdAt,
    created_at_end: createdAtBounds.dateEnd,
    created_at_operator: state.filters.createdAtOperator,
    created_at_start: createdAtBounds.dateStart,
    created_by: state.filters.createdBy,
    due_date: state.filters.dueDate,
    due_date_end: dueDateBounds.dateEnd,
    due_date_operator: state.filters.dueDateOperator,
    due_date_start: dueDateBounds.dateStart,
    project: state.filters.project,
    severity: state.filters.severity,
    status: state.filters.status,
    tag: state.filters.tag,
    tag_match: state.filters.tagMatch,
    visibility: state.filters.visibility
  };
};

const formatDate = (value?: string) => (value ? value.slice(0, 10) : '');

const legacyDateFilter = (
  start?: string,
  end?: string
): { date: string; operator: ProjectSearchDateOperator } => {
  const startDate = formatDate(start);
  const endDate = formatDate(end);
  if (startDate && endDate && startDate === endDate) {
    return { date: startDate, operator: 'on' };
  }
  if (startDate) return { date: startDate, operator: 'after' };
  if (endDate) return { date: endDate, operator: 'before' };
  return { date: '', operator: 'on' };
};

export const dashboardFiltersAsLegacyFilter = (
  filters: DashboardTaskSearchFilters
): UserTaskFilter => {
  const legacyFilter: UserTaskFilter = {};
  if (filters.project.length) legacyFilter.projectId = filters.project;
  if (filters.createdBy.length) legacyFilter.requesterId = filters.createdBy;
  if (filters.assignee.length) legacyFilter.assignedUserId = filters.assignee;
  if (filters.severity.length) legacyFilter.priorityId = filters.severity;
  if (filters.status.length) legacyFilter.status = filters.status;

  if (filters.createdAt) {
    if (filters.createdAtOperator !== 'before') {
      legacyFilter.createdAtStart = filters.createdAt;
    }
    if (filters.createdAtOperator !== 'after') {
      legacyFilter.createdAtEnd = filters.createdAt;
    }
  }
  if (filters.dueDate) {
    if (filters.dueDateOperator !== 'before') {
      legacyFilter.dueAtStart = filters.dueDate;
    }
    if (filters.dueDateOperator !== 'after') {
      legacyFilter.dueAtEnd = filters.dueDate;
    }
  }

  return legacyFilter;
};

export const mergeLegacyFilterIntoDashboardFilters = (
  filters: DashboardTaskSearchFilters,
  legacyFilter: UserTaskFilter = {}
): DashboardTaskSearchFilters => {
  const createdAt = legacyDateFilter(
    legacyFilter.createdAtStart,
    legacyFilter.createdAtEnd
  );
  const dueDate = legacyDateFilter(
    legacyFilter.dueAtStart,
    legacyFilter.dueAtEnd
  );

  return {
    ...filters,
    project: legacyFilter.projectId || [],
    assignee: legacyFilter.assignedUserId || [],
    createdAt: createdAt.date,
    createdAtOperator: createdAt.operator,
    createdBy: legacyFilter.requesterId || [],
    dueDate: dueDate.date,
    dueDateOperator: dueDate.operator,
    severity: legacyFilter.priorityId || [],
    status: legacyFilter.status || []
  };
};

export const dashboardSortAsTaskOrder = (
  sortField: DashboardTaskSortField,
  sortDirection: OrderBy
): UserTaskOrder => ({ [ENDPOINT_SORT_FIELDS[sortField]]: sortDirection });

export const taskOrderAsDashboardSort = (
  taskOrder: UserTaskOrder
): Pick<DashboardTaskSearchState, 'sortField' | 'sortDirection'> => {
  const [tableField, sortDirection] = Object.entries(taskOrder)[0] || [];
  return {
    sortField: TABLE_SORT_FIELDS[tableField] || 'project_name',
    sortDirection: sortDirection === OrderBy.DESC ? OrderBy.DESC : OrderBy.ASC
  };
};

export const useDashboardTaskSearch = ({
  organizationId,
  currentUserId,
  config,
  enabled,
  perPage
}: {
  organizationId: string;
  currentUserId: string;
  config: Config;
  enabled: boolean;
  perPage: number;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = React.useMemo(
    () => getDashboardTaskSearchState(location.search),
    [location.search]
  );
  const stateRef = React.useRef(state);
  const locationRef = React.useRef(location);
  stateRef.current = state;
  locationRef.current = location;

  const [draftQuery, setDraftQuery] = React.useState(state.query);
  const [response, setResponse] = React.useState<
    DashboardTaskSearchResponse | undefined
  >();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | undefined>();
  const [refreshCount, setRefreshCount] = React.useState(0);
  const activeRequestRef = React.useRef<AbortController | null>(null);
  const debounceTimerRef = React.useRef<number | null>(null);
  const skipDraftSyncRef = React.useRef(false);

  const request = React.useMemo(
    () => dashboardTaskSearchRequest(state, perPage),
    [state, perPage]
  );
  const requestKey = JSON.stringify(request);

  React.useEffect(() => {
    if (skipDraftSyncRef.current) {
      skipDraftSyncRef.current = false;
      return;
    }
    setDraftQuery(state.query);
  }, [state.query]);

  const clearDebounceTimer = React.useCallback(() => {
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (!enabled) {
      if (activeRequestRef.current) activeRequestRef.current.abort();
      activeRequestRef.current = null;
      return;
    }

    if (activeRequestRef.current) activeRequestRef.current.abort();
    const abortController = new AbortController();
    activeRequestRef.current = abortController;
    setLoading(true);
    setError(undefined);

    post(`/organizations/${organizationId}/task_search`, request, {
      signal: abortController.signal
    })
      .then(rawResponse => {
        if (activeRequestRef.current !== abortController) return;
        setResponse(
          camelCaseKeys(rawResponse as Record<string, unknown>, {
            deep: true
          }) as DashboardTaskSearchResponse
        );
      })
      .catch(requestError => {
        if (
          activeRequestRef.current === abortController &&
          (requestError as Error).name !== 'AbortError'
        ) {
          setError(requestError as Error);
        }
      })
      .finally(() => {
        if (activeRequestRef.current === abortController) {
          activeRequestRef.current = null;
          setLoading(false);
        }
      });

    return () => abortController.abort();
  }, [enabled, organizationId, refreshCount, requestKey]);

  React.useEffect(
    () => () => {
      clearDebounceTimer();
      if (activeRequestRef.current) activeRequestRef.current.abort();
    },
    [clearDebounceTimer]
  );

  const updateState = React.useCallback(
    (updates: Partial<DashboardTaskSearchState>, resetPage = false) => {
      const nextState = {
        ...stateRef.current,
        ...updates,
        page: resetPage ? 1 : updates.page || stateRef.current.page
      };
      if (enabled) {
        if (activeRequestRef.current) activeRequestRef.current.abort();
        activeRequestRef.current = null;
        setLoading(true);
        setError(undefined);
      }
      const currentLocation = locationRef.current;
      navigate(
        dashboardTaskSearchUrl({
          pathname: currentLocation.pathname,
          search: currentLocation.search,
          hash: currentLocation.hash,
          state: nextState
        })
      );
    },
    [enabled, navigate]
  );

  const commitQuery = React.useCallback(
    (query: string) => {
      const normalizedQuery = normalizeDashboardSearchQuery(query);
      if (stateRef.current.query === normalizedQuery) return;
      updateState({ query: normalizedQuery }, true);
    },
    [updateState]
  );

  const setQuery = React.useCallback(
    (query: string) => {
      setDraftQuery(query);
      clearDebounceTimer();

      const normalizedQuery = normalizeDashboardSearchQuery(query);

      if (normalizedQuery === '') {
        commitQuery('');
        return;
      }

      if (!isDashboardAutoSearchQueryValid(normalizedQuery)) {
        // Sub-threshold auto input: keep draft only. Clear a prior committed
        // query so stale results do not linger, but do not start loading when
        // nothing was committed yet (e.g. typing two characters from empty).
        if (stateRef.current.query !== '') {
          skipDraftSyncRef.current = true;
          commitQuery('');
        } else if (activeRequestRef.current) {
          activeRequestRef.current.abort();
          activeRequestRef.current = null;
          setLoading(false);
        }
        return;
      }

      debounceTimerRef.current = window.setTimeout(() => {
        debounceTimerRef.current = null;
        commitQuery(normalizedQuery);
      }, DASHBOARD_SEARCH_DEBOUNCE_MS);
    },
    [clearDebounceTimer, commitQuery]
  );

  const submitQuery = React.useCallback(
    (query: string = draftQuery) => {
      clearDebounceTimer();
      setDraftQuery(query);

      const normalizedQuery = normalizeDashboardSearchQuery(query);

      if (normalizedQuery === '') {
        commitQuery('');
        return;
      }

      if (!isDashboardSubmitSearchQueryValid(normalizedQuery)) {
        if (activeRequestRef.current) {
          activeRequestRef.current.abort();
          activeRequestRef.current = null;
          setLoading(false);
        }
        return;
      }

      commitQuery(normalizedQuery);
    },
    [clearDebounceTimer, commitQuery, draftQuery]
  );

  const data = React.useMemo<GetUserTasksData | undefined>(
    () =>
      response && {
        config,
        currentUser: {
          id: currentUserId,
          userTasks: response.tasks.map(task => ({
            ...task,
            id: String(task.id)
          }))
        }
      },
    [config, currentUserId, response]
  );
  const refetch = React.useCallback(
    () => setRefreshCount(count => count + 1),
    []
  );

  const clearSearch = React.useCallback(() => {
    clearDebounceTimer();
    setDraftQuery('');
    updateState(
      { query: '', filters: emptyDashboardTaskSearchFilters() },
      true
    );
  }, [clearDebounceTimer, updateState]);

  return {
    state,
    draftQuery,
    data,
    pagination: response?.pagination,
    loading,
    error,
    refetch,
    setQuery,
    submitQuery,
    setFilters: (filters: DashboardTaskSearchFilters) =>
      updateState({ filters }, true),
    setScope: (scope: DashboardTaskScope) => updateState({ scope }, true),
    setSort: (taskOrder: UserTaskOrder) =>
      updateState(taskOrderAsDashboardSort(taskOrder), true),
    setPage: (page: number) => updateState({ page }),
    clearSearch
  };
};
